import React, { useEffect, useState, useCallback } from "react";
import api, { downloadAccessLogPdf } from "../../api";
import { toast } from "sonner";
import { Monitor, Smartphone, Tablet, Search, FileDown, CheckCircle2, XCircle } from "lucide-react";

const PAGE = 50;

const DEVICE_META = {
  pc: { label: "PC", cls: "bg-slate-100 text-slate-700", icon: Monitor },
  mobile: { label: "Cellulare", cls: "bg-indigo-100 text-indigo-700", icon: Smartphone },
  tablet: { label: "Tablet", cls: "bg-amber-100 text-amber-700", icon: Tablet },
};

const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const selectCls = "border border-gray-300 rounded-sm px-3 py-2 text-[13px] outline-none focus:border-[#16345c] bg-white";

export const AccessLogTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const query = useCallback((sk) => `?limit=${PAGE}&skip=${sk}&device=${device}&outcome=${outcome}&q=${encodeURIComponent(q)}`, [device, outcome, q]);

  const load = useCallback((sk = 0, append = false) => {
    setLoading(true);
    api.get(`/access-logs${query(sk)}`)
      .then((r) => {
        setTotal(r.data.total || 0);
        setSkip(sk);
        setItems((prev) => (append ? [...prev, ...(r.data.items || [])] : (r.data.items || [])));
      })
      .catch(() => toast.error("Errore nel caricamento accessi"))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => { load(0, false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [device, outcome]);

  const doSearch = (e) => { e.preventDefault(); load(0, false); };

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadAccessLogPdf(`?device=${device}&outcome=${outcome}&q=${encodeURIComponent(q)}`, "registro_accessi.pdf");
      toast.success("Registro accessi scaricato");
    } catch { toast.error("Errore durante il download"); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="access-log-tab">
      <div className="flex items-center gap-2 mb-4 text-[13px] text-gray-600">
        Storico cronologico degli accessi al portale (riusciti e falliti), con utente e dispositivo (PC, Cellulare, Tablet).
      </div>

      <form onSubmit={doSearch} className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[280px]">
          <Search size={16} className="text-gray-400" />
          <input value={q} data-testid="access-search" onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, matricola o username..." className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <select value={device} onChange={(e) => setDevice(e.target.value)} data-testid="access-filter-device" className={selectCls}>
          <option value="all">Tutti i dispositivi</option>
          <option value="pc">PC</option>
          <option value="mobile">Cellulare</option>
          <option value="tablet">Tablet</option>
        </select>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="access-filter-outcome" className={selectCls}>
          <option value="all">Tutti gli esiti</option>
          <option value="ok">Riusciti</option>
          <option value="fallito">Falliti</option>
        </select>
        <button type="submit" data-testid="access-search-btn" className="bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors">Cerca</button>
        <button type="button" onClick={exportPdf} disabled={exporting} data-testid="access-export-pdf" className="ml-auto flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c] hover:text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60"><FileDown size={15} /> {exporting ? "..." : "Report PDF"}</button>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="access-log-table">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Data / Ora</th>
              <th className="text-left px-4 py-3 font-bold">Utente</th>
              <th className="text-left px-4 py-3 font-bold">Dispositivo</th>
              <th className="text-left px-4 py-3 font-bold">Esito</th>
              <th className="text-left px-4 py-3 font-bold">Indirizzo IP</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const dev = DEVICE_META[it.device] || { label: it.device || "—", cls: "bg-gray-100 text-gray-600", icon: Monitor };
              const DevIcon = dev.icon;
              const ok = it.outcome === "ok";
              return (
                <tr key={it.id} data-testid={`access-row-${it.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-[13px] text-gray-700 whitespace-nowrap">{fmtDateTime(it.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-[14px] font-bold text-[#16345c]">{it.employee_name || it.username || "—"}</div>
                    <div className="text-[11px] text-gray-500">{it.matricola || it.username}{it.reason && !ok ? ` · ${it.reason}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${dev.cls}`}><DevIcon size={12} /> {dev.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {ok ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700"><CheckCircle2 size={12} /> Riuscito</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><XCircle size={12} /> Fallito</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12.5px] text-gray-500 font-mono">{it.ip || "—"}</td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessun accesso registrato con i filtri selezionati.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-[12px] text-gray-500" data-testid="access-count">{items.length} di {total} accessi</span>
        {items.length < total && (
          <button onClick={() => load(skip + PAGE, true)} disabled={loading} data-testid="access-load-more" className="text-[13px] font-bold text-[#16345c] border border-[#16345c] hover:bg-[#16345c] hover:text-white px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
            {loading ? "Caricamento..." : "Carica altri"}
          </button>
        )}
      </div>
    </div>
  );
};
